import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { updateUserAdmin, fetchUsersAdmin } from '../../store/users';

const EditUserForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const user = useSelector(state => state.users.find(u => u.id === parseInt(id)));
  const sessionUser = useSelector(state => state.session.user);

  const [formData, setFormData] = useState({
    alpha_code: '',
    email: '',
    driver_number: '',
    password: '',
    confirmPassword: '',
    is_admin: false,
    is_super_manager: false,
    is_account_manager: false,
    is_manager: false,
    is_road_driver: false,
    is_yard_driver: false,
    is_mechanic: false,
    is_security: false,
  });

  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        alpha_code: user.alpha_code || '',
        email: user.email || '',
        driver_number: user.driver_number || '',
        password: '',
        confirmPassword: '',
        is_admin: user.is_admin || false,
        is_super_manager: user.is_super_manager || false,
        is_account_manager: user.is_account_manager || false,
        is_manager: user.is_manager || false,
        is_road_driver: user.is_road_driver || false,
        is_yard_driver: user.is_yard_driver || false,
        is_mechanic: user.is_mechanic || false,
        is_security: user.is_security || false,
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      dispatch(fetchUsersAdmin());
    }
  }, [dispatch, user, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = [];

    if (formData.password || formData.confirmPassword) {
      if (formData.password !== formData.confirmPassword) {
        validationErrors.push('Password and Confirm Password must match');
      }
      if (formData.password.length < 6) {
        validationErrors.push('Password must be at least 6 characters long');
      }
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Only include password in userData if it's been changed
    const userData = { ...formData };
    if (!userData.password) {
      delete userData.password;
    }
    delete userData.confirmPassword;

    try {
      const updatedUser = await dispatch(updateUserAdmin(id, userData));
      if (updatedUser) {
        history.push(`/users/${id}`);
      }
    } catch (error) {
      setErrors([error.message || 'Failed to update user']);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name.startsWith('is_')) {
      // Reset all roles first, then set the selected one
      const resetRoles = Object.keys(formData)
        .filter(key => key.startsWith('is_'))
        .reduce((acc, key) => ({ ...acc, [key]: false }), {});

      setFormData(prev => ({
        ...prev,
        ...resetRoles,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Add role management functions from SignupFormPage
  const getUserRoleLevel = (user) => {
    if (user.is_admin) return 5;
    if (user.is_super_manager) return 4;
    if (user.is_account_manager) return 3;
    if (user.is_manager) return 2;
    return 1;
  };

  const getAvailableRoles = () => {
    const userLevel = getUserRoleLevel(sessionUser);
    const roles = [
      { name: 'is_admin', label: 'Admin', level: 5 },
      { name: 'is_super_manager', label: 'Super Manager', level: 4 },
      { name: 'is_account_manager', label: 'Account Manager', level: 3 },
      { name: 'is_manager', label: 'Manager', level: 2 },
      { name: 'is_road_driver', label: 'Road Driver', level: 1 },
      { name: 'is_yard_driver', label: 'Yard Driver', level: 1 },
      { name: 'is_mechanic', label: 'Mechanic', level: 1 },
      { name: 'is_security', label: 'Security', level: 1 },
    ];

    return sessionUser.is_admin ? roles : roles.filter(role => role.level < userLevel);
  };

  if (!user) {
    return <div className="p-5 text-7xl">Loading...</div>;
  }

  return (
    <div className="p-5 min-h-[calc(100vh-100px)]">
      <h1 className="text-8xl font-bold mb-10 text-center text-blue-600">Edit User</h1>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 p-4 rounded-lg">
            {errors.map((error, idx) => (
              <p key={idx} className="text-red-500 text-xl">{error}</p>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column - User Information */}
          <div className="space-y-6">
            <h2 className="text-4xl font-semibold mb-6 text-gray-800">User Information</h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="email-input" className="block text-2xl font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {user.alpha_code && (
                <div>
                  <label htmlFor="alpha-code-input" className="block text-2xl font-medium text-gray-700 mb-2">
                    Alpha Code
                  </label>
                  <input
                    id="alpha-code-input"
                    type="text"
                    name="alpha_code"
                    value={formData.alpha_code}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              )}

              {user.driver_number && (
                <div>
                  <label htmlFor="driver-number-input" className="block text-2xl font-medium text-gray-700 mb-2">
                    Driver Number
                  </label>
                  <input
                    id="driver-number-input"
                    type="text"
                    name="driver_number"
                    value={formData.driver_number}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              )}

              {/* Password fields are always shown as they can be updated */}
              <div>
                <label htmlFor="password-input" className="block text-2xl font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  id="password-input"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  placeholder="Leave blank to keep current password"
                />
                <p className="text-sm text-gray-500 mt-1">New users are assigned a default password of "Welcome1". You can change your password here.</p>
              </div>

              <div>
                <label htmlFor="confirm-password-input" className="block text-2xl font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  id="confirm-password-input"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  placeholder="Leave blank to keep current password"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Roles */}
          <div>
            <h2 className="text-4xl font-semibold mb-6 text-gray-800">User Role</h2>
            <div className="space-y-4 bg-gray-50 p-6 rounded-lg">
              {getAvailableRoles().map(role => (
                <div key={role.name} className="flex items-center bg-white p-3 rounded-lg shadow-sm">
                  <input
                    type="radio"
                    id={role.name}
                    name="role"
                    checked={formData[role.name]}
                    onChange={() => {
                      const resetRoles = Object.keys(formData)
                        .filter(key => key.startsWith('is_'))
                        .reduce((acc, key) => ({ ...acc, [key]: false }), {});

                      setFormData(prev => ({
                        ...prev,
                        ...resetRoles,
                        [role.name]: true
                      }));
                    }}
                    className="h-6 w-6 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor={role.name} className="ml-3 text-2xl text-gray-700">
                    {role.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Buttons */}
        <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
          <button
            type="button"
            onClick={() => history.push(`/users/${id}`)}
            className="px-6 py-3 text-2xl text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Cancel
          </button>
          <button id='save-user-button'
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 text-2xl text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 disabled:bg-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditUserForm;
